import { useCallback, useState } from 'react';
import { type KeyedMutator, useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { type ReportWithEvents } from '@/types/report';

interface UseReportEventRemovalResult {
    removeEventIdPending: string | undefined;
    removeInProgress: boolean;
    requestRemove: (eventId: string) => void;
    cancelRemove: () => void;
    confirmRemove: () => Promise<void>;
}

export function useReportEventRemoval(
    mutate: KeyedMutator<ReportWithEvents>,
    onRemoved?: (removedEventId: string) => void,
): UseReportEventRemovalResult {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { mutate: mutateCache } = useSWRConfig();
    const [removeEventIdPending, setRemoveEventIdPending] = useState<
        string | undefined
    >();
    const [removeInProgress, setRemoveInProgress] = useState(false);

    const requestRemove = useCallback((eventId: string) => {
        setRemoveEventIdPending(eventId);
    }, []);

    const cancelRemove = useCallback(() => {
        setRemoveEventIdPending(undefined);
    }, []);

    const confirmRemove = useCallback(async () => {
        if (removeInProgress || !removeEventIdPending) {
            return;
        }
        const removedEventId = removeEventIdPending;
        setRemoveInProgress(true);
        try {
            await EngineService.removeReportEvent(removedEventId);
            toast.success(t('detail.removeEventSuccess'));
            setRemoveEventIdPending(undefined);
            await Promise.all([
                mutate(),
                mutateCache(
                    (key) =>
                        Array.isArray(key) &&
                        key[0] === CACHE_KEYS.REPORTS_LIST,
                    undefined,
                    { revalidate: true },
                ),
            ]);
            onRemoved?.(removedEventId);
        } catch {
            toast.error(t('detail.removeEventError'));
        } finally {
            setRemoveInProgress(false);
        }
    }, [
        mutate,
        mutateCache,
        onRemoved,
        removeInProgress,
        removeEventIdPending,
        t,
    ]);

    return {
        removeEventIdPending,
        removeInProgress,
        requestRemove,
        cancelRemove,
        confirmRemove,
    };
}
